import * as RadixPopover from "@radix-ui/react-popover";
import { CaretDown, Check, X } from "@phosphor-icons/react";
import { forwardRef, Fragment, type KeyboardEvent, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { kfContentIn, twTransform } from "@/lib/emotionTw";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import { floatingCollisionPadding } from "@/components/ui/overlays/overlayPositioning";
import { Button, type ButtonVariant } from "@/components/primitives/Button";
import { Spinner } from "../../feedback/Spinner";
import { groupSelectOptions, type SelectMenuWidth, type SelectOption } from "../Select";

/**
 * When the value is committed back to the consumer via `onValueChange`:
 * - `"change"` (default) — on every toggle, immediately.
 * - `"apply"` — only when the user confirms with the Apply button (or a custom
 *   footer action). Toggles update an internal draft; closing without applying
 *   discards the draft. An Apply/Cancel footer is shown automatically.
 */
export type MultiSelectCommitMode = "change" | "apply";
export type MultiSelectVariant = "default" | "filter";

export type MultiSelectProps = {
  options: SelectOption[];
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;
  /** Fired with the committed value when the user applies (apply mode only). */
  onApply?: (value: string[]) => void;
  placeholder?: string;
  /** `filter` removes the search field for compact filter dropdowns. */
  variant?: MultiSelectVariant;
  /** Override whether the popover renders the search field. Defaults to false for `variant="filter"`, true otherwise. */
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyText?: string;
  loading?: boolean;
  disabled?: boolean;
  error?: boolean;
  clearable?: boolean;
  size?: "sm" | "md";
  /** Option popover width relative to the trigger. Defaults to `"auto"` (the
   * popover grows to fit its content and can be wider than the trigger). */
  menuWidth?: SelectMenuWidth;
  /** Defer committing the value until applied. Defaults to `"change"`. */
  commitMode?: MultiSelectCommitMode;
  /** Custom popover footer. Overrides the default Apply/Cancel footer. */
  footer?: ReactNode;
  applyLabel?: string;
  cancelLabel?: string;
  clearLabel?: string;
  /** Button variant for the default footer Apply action. Defaults to `"outline"`. */
  applyVariant?: ButtonVariant;
  /** Button variant for the default footer Cancel action. Defaults to `"outline"`. */
  cancelVariant?: ButtonVariant;
  clearVariant?: ButtonVariant;
  className?: string;
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
};

const uniqueValues = (values: string[]) => Array.from(new Set(values));

type MultiSelectSize = "sm" | "md";

interface MultiSelectTriggerOwnerState {
  size: MultiSelectSize;
  error: boolean;
  hasSelection: boolean;
}

/* Styles are the exact compiled output of the pre-migration Tailwind
 * utilities, emitted in the original class-list order. Verified against the
 * frozen baseline build, except the trigger active ring/outline was removed so
 * focused/open select fields render a single border. */

const msTriggerBase: CSSObject = {
  display: "flex",
  width: "100%",
  alignItems: "center",
  justifyContent: "space-between",
  gap: ".5rem",
  borderRadius: "var(--bfrs-radius-lg)",
  borderWidth: "1px",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  textAlign: "left",
  fontWeight: 400,
  outline: "none",
  transitionProperty:
    "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  transitionDuration: ".15s",
  "&:disabled": {
    cursor: "not-allowed",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
    WebkitTextFillColor: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  }
};

/* Width-stability guard — intentional divergence from the frozen Tailwind
 * output above. When the consumer gives the field no width constraint, the
 * trigger sits in a shrink-to-fit parent whose width tracks the trigger's
 * intrinsic content; every selection pill then widens the field. `contain:
 * inline-size` stops the pills from feeding the trigger's own inline size — it
 * is inert once the width is definite, so width-constrained layouts (including
 * the parity fixtures, which render inside 280px parents) are pixel-identical
 * to before. The min-width floor keeps the trigger from collapsing to just its
 * controls when the parent offers no width to fill; below the floor the pills
 * truncate and overflow into the `+N` pill instead of growing the field. */
const msTriggerWidthGuard: CSSObject = {
  minWidth: "12rem",
  contain: "inline-size"
};

const msTriggerSize: Record<MultiSelectSize, CSSObject> = {
  sm: { borderRadius: "7px", paddingLeft: ".75rem", paddingRight: ".75rem", fontSize: "12px", height: "1.75rem" },
  md: { borderRadius: "7px", paddingLeft: ".75rem", paddingRight: ".75rem", fontSize: "12px", height: "2.25rem" }
};

const msTriggerError: CSSObject = {
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))",
  "&:focus-visible": {
    borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))"
  },
  '&[data-state="open"]': {
    borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))"
  },
  '&[aria-expanded="true"]': {
    borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))"
  }
};

const msTriggerDefaultBorder = (size: MultiSelectSize): CSSObject => ({
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  "&:focus-visible": {
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))"
  },
  '&[data-state="open"]': {
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))"
  },
  '&[aria-expanded="true"]': {
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))"
  },
  ...(size === "sm"
    ? {
        "&[data-filled]": {
          borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))"
        }
      }
    : null)
});

const MultiSelectTrigger = styled("button", {
  label: "AgenticMultiSelect-trigger",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: MultiSelectTriggerOwnerState }>(({ ownerState }) => [
  msTriggerBase,
  msTriggerWidthGuard,
  msTriggerSize[ownerState.size],
  ownerState.error ? msTriggerError : msTriggerDefaultBorder(ownerState.size),
  ownerState.hasSelection
    ? {
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
      }
    : {
        "--tw-text-opacity": "1",
        color: "rgb(var(--bfrs-color-text-placeholder) / var(--tw-text-opacity, 1))"
      }
]);

const MultiSelectValueArea = styled("span", {
  label: "AgenticMultiSelect-valueArea"
})({
  display: "flex",
  minWidth: "0px",
  flex: "1 1 0%",
  alignItems: "center",
  gap: ".375rem",
  overflow: "hidden"
});

const MultiSelectValuePill = styled("span", {
  label: "AgenticMultiSelect-valuePill"
})({
  display: "inline-flex",
  maxWidth: "9rem",
  // Value pills yield space first (unlike the overflow pill below, which stays
  // flex-shrink: 0): under width pressure they shrink and their label ellipsizes
  // via MultiSelectTruncate, so the protected "+N" pill is never clipped.
  flexShrink: 1,
  minWidth: 0,
  alignItems: "center",
  borderRadius: "var(--bfrs-radius-pill)",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))",
  paddingLeft: ".5rem",
  paddingRight: ".5rem",
  paddingTop: ".125rem",
  paddingBottom: ".125rem",
  fontSize: ".75rem",
  lineHeight: "1rem",
  fontWeight: 400,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))"
});

const MultiSelectOverflowPill = styled("span", {
  label: "AgenticMultiSelect-overflowPill"
})({
  flexShrink: 0,
  borderRadius: "var(--bfrs-radius-pill)",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-raised) / var(--tw-bg-opacity, 1))",
  paddingLeft: ".5rem",
  paddingRight: ".5rem",
  paddingTop: ".125rem",
  paddingBottom: ".125rem",
  fontSize: ".75rem",
  lineHeight: "1rem",
  fontWeight: 400,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
});

const truncateStyles: CSSObject = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
};

const MultiSelectTruncate = styled("span", {
  label: "AgenticMultiSelect-truncate"
  // minWidth: 0 lets the label shrink below its content inside a shrinking value
  // pill so the ellipsis engages instead of forcing the pill (and the row) wider.
})({ ...truncateStyles, minWidth: 0 });

const MultiSelectControls = styled("span", {
  label: "AgenticMultiSelect-controls"
})({
  display: "flex",
  flexShrink: 0,
  alignItems: "center",
  gap: ".25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const MultiSelectClear = styled("span", {
  label: "AgenticMultiSelect-clear"
})({
  borderRadius: "var(--bfrs-radius-sm)",
  padding: ".125rem",
  "&:hover": {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))"
  }
});

const MultiSelectCaret = styled(CaretDown, {
  label: "AgenticMultiSelect-caret",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { open: boolean } }>(({ ownerState }) => [
  {
    transitionProperty: "transform",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".15s"
  },
  ownerState.open && twTransform({ "--tw-rotate": "180deg" })
]);

const MultiSelectContent = styled(RadixPopover.Content, {
  label: "AgenticMultiSelect-content",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { menuWidth: SelectMenuWidth } }>(({ ownerState }) => [
  {
    zIndex: 1000,
    display: "flex",
    flexDirection: "column",
    borderRadius: "var(--bfrs-radius-lg)",
    borderWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
    padding: ".25rem",
    "--tw-shadow": "var(--bfrs-shadow-elevated)",
    "--tw-shadow-colored": "var(--bfrs-shadow-elevated)",
    boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)",
    outline: "2px solid transparent",
    outlineOffset: "2px",
    ...kfContentIn,
    animation: "bfrs-bfrs-content-in var(--bfrs-motion-normal) var(--bfrs-ease-out)"
  },
  ownerState.menuWidth === "trigger" && { width: "var(--radix-popover-trigger-width)" },
  ownerState.menuWidth === "auto" && { minWidth: "var(--radix-popover-trigger-width)" },
  // searchableSelectContentCollisionClassName: always sets the trigger width
  // (a numeric menuWidth wins via inline style, exactly as before).
  {
    maxHeight: "min(18rem,var(--radix-popover-content-available-height))",
    width: "var(--radix-popover-trigger-width)",
    overflow: "hidden"
  }
]);

const MultiSelectSearch = styled("input", {
  label: "AgenticMultiSelect-search"
})({
  marginBottom: ".25rem",
  height: "1.75rem",
  width: "100%",
  flexShrink: 0,
  borderRadius: "7px",
  borderWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  paddingLeft: ".75rem",
  paddingRight: ".75rem",
  fontSize: "12px",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
  "&::-moz-placeholder": {
    "--tw-text-opacity": "1",
    color: "rgb(var(--bfrs-color-text-placeholder) / var(--tw-text-opacity, 1))"
  },
  "&::placeholder": {
    "--tw-text-opacity": "1",
    color: "rgb(var(--bfrs-color-text-placeholder) / var(--tw-text-opacity, 1))"
  },
  outline: "none",
  "&:focus": {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))"
  },
  "&:focus-visible": {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))"
  }
});

const MultiSelectListbox = styled("div", {
  label: "AgenticMultiSelect-listbox"
})({
  minHeight: "0px",
  flex: "1 1 0%",
  maxHeight: "min(14rem,max(3rem,calc(var(--radix-popover-content-available-height) - 6.5rem)))",
  overflow: "auto"
});

const MultiSelectLoading = styled("div", {
  label: "AgenticMultiSelect-loading"
})({
  display: "flex",
  height: "4rem",
  alignItems: "center",
  justifyContent: "center",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const MultiSelectEmpty = styled("div", {
  label: "AgenticMultiSelect-empty"
})({
  paddingLeft: ".75rem",
  paddingRight: ".75rem",
  paddingTop: "1rem",
  paddingBottom: "1rem",
  fontSize: ".875rem",
  lineHeight: "1.25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const MultiSelectGroupLabel = styled("div", {
  label: "AgenticMultiSelect-groupLabel"
})({
  paddingLeft: ".75rem",
  paddingRight: ".75rem",
  paddingBottom: ".25rem",
  paddingTop: ".5rem",
  fontSize: "10px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: ".08em",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

interface MultiSelectOptionOwnerState {
  selected: boolean;
  active: boolean;
}

const MultiSelectOption = styled("button", {
  label: "AgenticMultiSelect-option",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: MultiSelectOptionOwnerState }>(({ ownerState }) => [
  {
    display: "flex",
    width: "100%",
    cursor: "pointer",
    alignItems: "center",
    gap: ".625rem",
    borderRadius: "var(--bfrs-radius-sm)",
    paddingLeft: ".75rem",
    paddingRight: ".75rem",
    paddingTop: ".5rem",
    paddingBottom: ".5rem",
    textAlign: "left",
    fontSize: "12px",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
    outline: "2px solid transparent",
    outlineOffset: "2px",
    "&:hover": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))"
    },
    "&:disabled": {
      cursor: "not-allowed",
      "--tw-text-opacity": "1",
      color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
    }
  },
  ownerState.selected && {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))",
    fontWeight: 700
  },
  ownerState.active && {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))"
  }
]);

const MultiSelectCheckbox = styled("span", {
  label: "AgenticMultiSelect-checkbox",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { selected: boolean } }>(({ ownerState }) => [
  {
    display: "flex",
    width: "1rem",
    height: "1rem",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "var(--bfrs-radius-sm)",
    borderWidth: "1px"
  },
  ownerState.selected
    ? {
        "--tw-border-opacity": "1",
        borderColor: "hsl(var(--bfrs-color-primary) / var(--tw-border-opacity, 1))",
        "--tw-bg-opacity": "1",
        backgroundColor: "hsl(var(--bfrs-color-primary) / var(--tw-bg-opacity, 1))",
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-primary-contrast) / var(--tw-text-opacity, 1))"
      }
    : {
        "--tw-border-opacity": "1",
        borderColor: "hsl(var(--bfrs-color-form-control-border) / var(--tw-border-opacity, 1))",
        "--tw-bg-opacity": "1",
        backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))"
      }
]);

const MultiSelectOptionLabel = styled("span", {
  label: "AgenticMultiSelect-optionLabel"
})({
  minWidth: "0px",
  flex: "1 1 0%",
  ...truncateStyles
});

const MultiSelectCustomFooter = styled("div", {
  label: "AgenticMultiSelect-customFooter"
})({
  marginTop: ".25rem",
  flexShrink: 0,
  borderTopWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  paddingTop: ".25rem"
});

const MultiSelectFooter = styled("div", {
  label: "AgenticMultiSelect-footer"
})({
  marginTop: ".25rem",
  display: "flex",
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "flex-end",
  gap: ".5rem",
  borderTopWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  paddingLeft: ".25rem",
  paddingRight: ".25rem",
  paddingBottom: ".25rem",
  paddingTop: ".5rem"
});

const MultiSelectFooterButton = styled(Button, {
  label: "AgenticMultiSelect-footerButton"
})({
  width: "5rem"
});

export const MultiSelect = forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      options,
      value,
      defaultValue = [],
      onValueChange,
      onApply,
      placeholder = "Select",
      variant = "default",
      searchable,
      searchPlaceholder = "Search...",
      emptyText = "No results found",
      loading,
      disabled,
      error,
      clearable = true,
      size = "md",
      menuWidth = "auto",
      commitMode = "change",
      footer,
      applyLabel = "Apply",
      cancelLabel,
      clearLabel,
      applyVariant = "outline",
      cancelVariant,
      clearVariant,
      className,
      id,
      "aria-describedby": describedBy,
      "aria-invalid": ariaInvalid
    },
    ref
  ) => {
    const isApplyMode = commitMode === "apply";
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [activeIndex, setActiveIndex] = useState(0);
    const [uncontrolledValue, setUncontrolledValue] = useState<string[]>(() => uniqueValues(defaultValue));
    // Draft holds the in-progress selection in apply mode; the committed value is
    // only written when the user applies.
    const [draft, setDraft] = useState<string[]>(() => uniqueValues(defaultValue));
    const inputRef = useRef<HTMLInputElement>(null);
    const resolvedSearchable = searchable ?? variant !== "filter";

    const committedValues = useMemo(() => uniqueValues(value ?? uncontrolledValue), [value, uncontrolledValue]);
    // Values reflected by the option checkmarks: the draft while applying, else committed.
    const workingValues = isApplyMode ? draft : committedValues;
    const workingSet = useMemo(() => new Set(workingValues), [workingValues]);

    const optionMap = useMemo(() => new Map(options.map((option) => [option.value, option])), [options]);
    const selectedOptions = committedValues.map((selectedValue) => optionMap.get(selectedValue) ?? { value: selectedValue, label: selectedValue });
    // Fixed display: always show exactly one value pill; every other selection
    // collapses into the "+N" overflow pill. This is intentionally not
    // configurable — the count is hard-coded and cannot be overridden by props.
    const visibleSelectedOptions = selectedOptions.slice(0, 1);
    const hiddenSelectedCount = Math.max(selectedOptions.length - visibleSelectedOptions.length, 0);
    const filteredOptions = useMemo(
      () => (resolvedSearchable ? options.filter((option) => option.label.toLowerCase().includes(query.toLowerCase())) : options),
      [options, query, resolvedSearchable]
    );
    const filteredGroups = useMemo(() => groupSelectOptions(filteredOptions), [filteredOptions]);

    // Keep the draft synced to the committed value whenever the popover is closed,
    // so opening always starts from the current committed selection.
    useEffect(() => {
      if (!open) setDraft(committedValues);
    }, [open, committedValues]);

    const writeValue = (nextValue: string[]) => {
      const normalizedValue = uniqueValues(nextValue);
      if (value === undefined) setUncontrolledValue(normalizedValue);
      onValueChange?.(normalizedValue);
    };

    const handleOpenChange = (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (nextOpen) {
        setDraft(committedValues);
      } else {
        // Closing without applying discards the draft (handled by the effect above).
        setQuery("");
        setActiveIndex(0);
      }
    };

    const toggleValue = (nextValue: string) => {
      const next = workingSet.has(nextValue)
        ? workingValues.filter((selectedValue) => selectedValue !== nextValue)
        : [...workingValues, nextValue];

      if (isApplyMode) {
        setDraft(uniqueValues(next));
      } else {
        writeValue(next);
      }
    };

    const applyDraft = () => {
      writeValue(draft);
      onApply?.(uniqueValues(draft));
      handleOpenChange(false);
    };

    const resolvedCancelLabel = cancelLabel ?? clearLabel ?? "Cancel";
    const resolvedCancelVariant = cancelVariant ?? clearVariant ?? "outline";

    const cancelDraft = () => {
      setDraft(committedValues);
      handleOpenChange(false);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
      if (!open && ["ArrowDown", "Enter", " "].includes(event.key)) {
        event.preventDefault();
        handleOpenChange(true);
        return;
      }

      if (!open) return;

      const eventTarget = event.target instanceof HTMLElement ? event.target : null;
      if (event.key === "Enter" && eventTarget?.closest("[data-bfrs-multi-select-option]")) {
        return;
      }

      if (event.key === "Escape") handleOpenChange(false);
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex((index) => Math.min(index + 1, Math.max(filteredOptions.length - 1, 0)));
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((index) => Math.max(index - 1, 0));
      }
      if (event.key === "Enter" && filteredOptions[activeIndex] && !filteredOptions[activeIndex].disabled) {
        event.preventDefault();
        toggleValue(filteredOptions[activeIndex].value);
      }
    };

    const hasSelection = selectedOptions.length > 0;
    const menuWidthStyle = typeof menuWidth === "number" ? { width: menuWidth, minWidth: menuWidth } : undefined;
    const showDefaultFooter = isApplyMode && footer === undefined;

    const renderOption = (option: SelectOption, index: number) => {
      const selected = workingSet.has(option.value);
      return (
        <MultiSelectOption
          key={option.value}
          type="button"
          role="option"
          data-bfrs-multi-select-option=""
          aria-selected={selected}
          disabled={option.disabled}
          ownerState={{ selected, active: activeIndex === index }}
          onMouseEnter={() => setActiveIndex(index)}
          onClick={() => toggleValue(option.value)}
        >
          <MultiSelectCheckbox ownerState={{ selected }} aria-hidden="true">
            {selected && <Check size={12} weight="bold" />}
          </MultiSelectCheckbox>
          <MultiSelectOptionLabel>{option.label}</MultiSelectOptionLabel>
        </MultiSelectOption>
      );
    };

    // Render grouped options while keeping a single flat index for keyboard nav.
    let flatIndex = -1;

    return (
      <RadixPopover.Root open={open} onOpenChange={handleOpenChange}>
        <RadixPopover.Trigger asChild>
          <MultiSelectTrigger
            ref={ref}
            id={id}
            type="button"
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-describedby={describedBy}
            aria-invalid={ariaInvalid ?? error}
            data-filled={hasSelection && !error ? "" : undefined}
            ownerState={{ size, error: Boolean(error), hasSelection }}
            className={className}
            onKeyDown={handleKeyDown}
          >
            <MultiSelectValueArea>
              {hasSelection ? (
                <>
                  {visibleSelectedOptions.map((option) => (
                    <MultiSelectValuePill key={option.value}>
                      <MultiSelectTruncate>{option.label}</MultiSelectTruncate>
                    </MultiSelectValuePill>
                  ))}
                  {hiddenSelectedCount > 0 && <MultiSelectOverflowPill>+{hiddenSelectedCount}</MultiSelectOverflowPill>}
                </>
              ) : (
                <MultiSelectTruncate>{placeholder}</MultiSelectTruncate>
              )}
            </MultiSelectValueArea>
            <MultiSelectControls>
              {clearable && hasSelection && (
                <MultiSelectClear
                  role="button"
                  tabIndex={-1}
                  aria-label="Clear selections"
                  onClick={(event) => {
                    event.stopPropagation();
                    writeValue([]);
                    setDraft([]);
                  }}
                >
                  <X size={14} />
                </MultiSelectClear>
              )}
              <MultiSelectCaret ownerState={{ open }} size={10} />
            </MultiSelectControls>
          </MultiSelectTrigger>
        </RadixPopover.Trigger>
        <RadixPopover.Portal>
          <BfrsPortalScope>
            <MultiSelectContent
              align="start"
              sideOffset={6}
              collisionPadding={floatingCollisionPadding}
              style={menuWidthStyle}
              ownerState={{ menuWidth }}
              onOpenAutoFocus={(event) => {
                event.preventDefault();
                if (resolvedSearchable) inputRef.current?.focus();
              }}
              onKeyDown={handleKeyDown}
            >
              {resolvedSearchable && (
                <MultiSelectSearch
                  ref={inputRef}
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    setActiveIndex(0);
                  }}
                  placeholder={searchPlaceholder}
                />
              )}
              <MultiSelectListbox role="listbox" aria-multiselectable="true">
                {loading ? (
                  <MultiSelectLoading>
                    <Spinner size="sm" />
                  </MultiSelectLoading>
                ) : filteredOptions.length === 0 ? (
                  <MultiSelectEmpty>{emptyText}</MultiSelectEmpty>
                ) : (
                  filteredGroups.map((grp, groupIndex) => (
                    <Fragment key={`group-${groupIndex}`}>
                      {grp.group && <MultiSelectGroupLabel>{grp.group}</MultiSelectGroupLabel>}
                      {grp.items.map((option) => {
                        flatIndex += 1;
                        return renderOption(option, flatIndex);
                      })}
                    </Fragment>
                  ))
                )}
              </MultiSelectListbox>
              {footer !== undefined ? (
                <MultiSelectCustomFooter>{footer}</MultiSelectCustomFooter>
              ) : showDefaultFooter ? (
                <MultiSelectFooter>
                  <MultiSelectFooterButton type="button" variant={resolvedCancelVariant} size="xs" onClick={cancelDraft}>
                    {resolvedCancelLabel}
                  </MultiSelectFooterButton>
                  <MultiSelectFooterButton type="button" variant={applyVariant} size="xs" onClick={applyDraft}>
                    {applyLabel}
                  </MultiSelectFooterButton>
                </MultiSelectFooter>
              ) : null}
            </MultiSelectContent>
          </BfrsPortalScope>
        </RadixPopover.Portal>
      </RadixPopover.Root>
    );
  }
);

MultiSelect.displayName = "MultiSelect";
